import React, { Suspense, useEffect, useRef, useState, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { Loader, Environment, OrthographicCamera } from '@react-three/drei';
import ReactAudioPlayer from 'react-audio-player';

import MicNoneIcon from '@mui/icons-material/MicNone';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StopCircle from '@mui/icons-material/StopCircle';
import { CircularProgress } from '@mui/material';

import {
  BrowserRouter,
  Routes,
  Route,
} from "react-router-dom";

import { chatHost, avatarUrl, silenceMp3, debug, backgroundImage3D, assetsUrl, ttsHost, domain } from './config.js';
import { sendToBot, restart, startTopic, sendFeedback, endSession, openInterimTextSocket, makeSpeech } from './messages.js';
import { splitIntoSentences, urlParam, cleanTextForSpeaking } from './utils.js';
import { doSpeechRecog, initSpeechRecognition } from './speechRecognition.js';

import SessionTableAccordion from './SessionTableAccordion'
import PersonalityForm from './PersonalityForm';
import IntroScreen from './IntroScreen';
import TopicIntro from './TopicIntro';
import WorkTopicIntro from './WorkTopicIntro';
import FeedbackDialog from './FeedbackDialog';
import QuestionForm from './Question';
import AudioTest from './AudioTest';

import { Avatar, Bg } from './Avatar';

const _ = require('lodash');

let bookList = [];

function AvatarScreen() {

  const audioPlayer = useRef();

  const [speak, setSpeak] = useState(false);
  const [text, setText] = useState('');
  const [audioSource, setAudioSource] = useState(null);
  const [playing, setPlaying] = useState(false);
  
  const speakingBuffer = useRef([]);
  const botHasSpoken = useRef(false);
  const [sessionEndRequested, setSessionEndRequested] = useState(false);
  const speechRecognitionControl = useRef(null);
  const startWithTopic = useRef(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [contentPreview, setContentPreview] = useState(null);
  const [speechBlendData, setSpeechBlendData] = useState(null);
  
  const stopSpeakingAfterThis = useRef(false);
  const messagesWithInterimResults = useRef(new Set());
  const lastMessageId = useRef(null);
  const interruptedMessages = useRef(new Set());
  const initialQuestion = useRef(urlParam('question'));
  const initialSay = useRef(urlParam('say'));
  const initialSayTitle = useRef(urlParam('sayTitle'));
  const initialBookId = useRef(urlParam('bookId'));
  const voice = useRef(urlParam('voice') || undefined);
  const ttsCache = useRef({});

  // System state:
  // loading - initial load
  // ready - ready to start the session
  // speaking - bot is speaking
  // recognizing - bot is listening
  // processing - understanding use message

  const LOADING =  'loading';
  const READY = 'ready';
  const SPEAKING = 'speaking';
  const RECOGNIZING = 'recognizing';
  const PROCESSING =  'processing';
  const FINISHED = 'finished';

  const [appState, _setAppState] = useState(READY);
  const appStateRef = useRef(READY); // This is needed to address the *current* state from callback, otherwise it's stale

  const suggestUrl = domain; // suggestTopic.current || domain;
  const topicToDiscuss = urlParam('topicToDiscuss') || 'this';
  const AFTER_MESSAGES = [
    `I\'m happy to talk to you in more depth about ${topicToDiscuss} and other emotional well-being topics.`,
    `Just go to: ${suggestUrl}`];

  const setAppState = (state) => {
    debug(`state: ${state}`);
    _setAppState(state);
    appStateRef.current = state;
  };
  
  const isSessionActive = () => [SPEAKING, RECOGNIZING, PROCESSING].includes(appState) && !sessionEndRequested;

  const cacheTTS = (text) => {
    makeSpeech(text, voice.current).then( response => {
      // Buffer the audio
      ttsCache.current[text] = response.data;
    });
  };

  function appendToSpeakingBuffer(_messages, clear = false) {
    // Force to objects, clean text
    const messages = _.map(
      _messages,
      (message)=> {
        if (typeof message === 'object') {
          message.text = cleanTextForSpeaking(message.text);
        } else {
          message = { text: cleanTextForSpeaking(message) };
        }
        return message;
      });
      
    if (clear) {
      speakingBuffer.current = messages;
    } else {
      speakingBuffer.current = _.concat(speakingBuffer.current || [], messages);
    }

    // Preload TTS for messages
    messages.forEach((message) => {
      cacheTTS(message.text);
    });
    
    const isSpeaking = appStateRef.current == SPEAKING;
    debug(`App state ${appStateRef.current}, isSpeaking ${isSpeaking}`);
    if (!isSpeaking || clear) {
      debug('Starting speech');
      setAppState(SPEAKING);
      sayNextMessage();
    }
  }

  function appendFinalMessages() {
    if (!stopSpeakingAfterThis.current) {
      stopSpeakingAfterThis.current = true;
      debug ('appending '+AFTER_MESSAGES);
      appendToSpeakingBuffer(AFTER_MESSAGES, false);      
      return true;
    }
    return false;
  }

  // Read the next message in speakingBuffer
  async function sayNextMessage() {
    // check if we have more messages left
    if (speakingBuffer.current.length > 0) { 
      const nextMessageObj = speakingBuffer.current.shift();
      const nextMessage = nextMessageObj.text;
      debug('Speaking: '+nextMessage);
      setText(nextMessage);
      setSpeak(true);
      if (nextMessageObj.preview) {
        setContentPreview(nextMessageObj.preview);
      } else {
        setContentPreview(null);
      }
      return true;
    }

    // no more messages left
    debug('no messages left');
    setSpeak(false);
    setText('');
    if (stopSpeakingAfterThis.current) {
      if (initialBookId.current && !_.isEmpty(bookList)) {
        window.localStorage[initialBookId.current] = true;
        const nextURL = bookList.find(u => !window.localStorage[new URLSearchParams(u.split('?')[1]).get('bookId')]);
        if (nextURL) {
          console.log(`Going to ${nextURL}`);
          window.location = nextURL;
        } else {
          console.log('Could not find next url');
        }
      }
      return true;
    }
  
    
    debug(`speak ${speak}, BHS ${botHasSpoken.current}`);
    if (speak == false && sessionEndRequested) {
      // end requested, we should already be in PROCESSING
      debug('Waiting for session summary...');
      return null;
    }
    

    // do speech recognition
    debug('starting speech recognition');
    
    const speechRecognitionCallback = (msg) => { 
      setAppState(PROCESSING);
      setText(msg);
      // Send recognized text to bot
      sendToBot(
        msg, 
        (messages) => {
          debug(`Received ${messages.length} new messages`);
          appendToSpeakingBuffer(messages); // this will changed the state
        },
        messagesWithInterimResults.current
      );
    };

    const onInterimRecognitionResultCallback = text => setText(text);
    speechRecognitionControl.current = await doSpeechRecog(speechRecognitionCallback, onInterimRecognitionResultCallback);
    setAppState(RECOGNIZING);
  }

  // Load the first message in conversation
  function getFirstMessage() {
    setAppState(PROCESSING);

    let startFn = null;
    if (startWithTopic.current) {
      startFn = callbackFn => startTopic(startWithTopic.current, callbackFn, messagesWithInterimResults.current);
    } else if (initialQuestion.current) {
      startFn = callbackFn => sendToBot(`/question ${initialQuestion.current}`, callbackFn, messagesWithInterimResults.current);
    } else if (initialBookId.current) {
      startFn = callbackFn => sendToBot(`/book ${initialBookId.current}`, callbackFn, messagesWithInterimResults.current);
    } else if (initialSay.current) {
      debug(initialSay.current);
      const sents = splitIntoSentences('Hello. '+initialSay.current);
      appendToSpeakingBuffer(sents, true);
      appendFinalMessages();
      return;
    } else {
      startFn = callbackFn => restart(callbackFn, messagesWithInterimResults.current);
    }
    
    // send  message to bot
    startFn((messages) => { 
      // Load message in temporary buffer
      appendToSpeakingBuffer(messages);
      // (possibly) append final messages
      if (initialQuestion.current || initialBookId.current) {
        // timeout to give a chance for the interim socket to return last message
        setTimeout(appendFinalMessages, 1000);
      }
    });
  }

  // Initial onload function
  useEffect(() => {
    document.body.style.backgroundColor = "black";
    window.localStorage['profile_completed'] = new Date();
    var start_topic = window.localStorage['start_topic'];
    if (start_topic) {
      startWithTopic.current = start_topic;
      window.localStorage['start_topic'] = '';
    }
    
    openInterimTextSocket((data) => {
      debug('Interim speech '+ data.text + ' for message '+data.messageId);
      lastMessageId.current = data.messageId;
      messagesWithInterimResults.current.add(data.messageId);

      if (!interruptedMessages.current.has(data.messageId)) {
        appendToSpeakingBuffer([data]);
      }
    });

    // Preload TTS for messages
    AFTER_MESSAGES.forEach((text) => {
      cacheTTS(text);
    });

    // if (initialQuestion.current) setTimeout(startSession, 5000);
    // if (initialBookId.current) setTimeout(startSession, 5000);
    if (initialBookId.current) {
      fetch("book_urls.txt").then((res) => res.text())
      .then((text) => {
        bookList = text.split('\n').map(l => l.trim());
        debug(bookList);
      })
      .catch((e) => console.error(e));
    }
  }, [])



  // Audio source has changed
  useEffect(() => {
    if (audioSource) {
      // Force load (necessary on some browsers)
      document.getElementsByClassName('react-audio-player')[0].load(); 
    }
  }, [audioSource])


  // End of playback
  function playerEnded(e) {
    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);

    debug(`audioSource ${audioSource}`);
    if (audioSource != silenceMp3) {
      botHasSpoken.current = true;
      setTimeout(sayNextMessage, 100); // Smaller value doesnt' work on mobile
    }
  }

  const getAudioPlayer = () => audioPlayer.current.audioEl.current;

  useEffect(() => {
    if (speak === false) return;
    if (!text) return;

    const speakBlendData = (blendData, filename) => {
      const audioSource = ttsHost + filename;
      setAudioSource(audioSource);
      getAudioPlayer().load();
      setSpeechBlendData(blendData);
    };

    const cached = ttsCache.current[text];
    if (cached) {
      const { blendData, filename} = cached;
      speakBlendData(blendData, filename);
    } else {
      // cached tts is not ready
      makeSpeech(text, voice.current)
      .then( response => {
        // Buffer the audio
        const { blendData, filename} = response.data;
        speakBlendData(blendData, filename);
      })
    }
  }, [speak]);

  // Player is ready
  function playerReady(e) {
    debug('Player ready');
    
    // Play audio
    getAudioPlayer().play();
    
    // Will cause animations to play
    setPlaying(true);
  }  


  function playSilence(callbackFn) {
    setAudioSource(silenceMp3);
    // setSpeak(true); 
    callbackFn();
  }

  function startSession() {
    // Play something after interaction
    // toget bypass autoplay protection on mobile ios
    playSilence(getFirstMessage);
  }

  async function onStartClick() {
    startSession();
    // await initSpeechRecognition();
    
  }

  function onEndClick() {    
    setSessionEndRequested(true);
    setSpeak(false);
    setPlaying(false);
    setAudioSource(null);

    setAppState(FINISHED);
    setSpeak(false);

    setFeedbackDialogOpen(true);

    setAppState(PROCESSING);
    endSession((messages) => {
      // setSpeakingBuffer(messages); 
    });
  }


  function onClickMicrophone() {
    speechRecognitionControl.current.stop();
  }

  function onInterruptClick() {
    setSpeak(false);
    setPlaying(false);
    getAudioPlayer().pause();
    setAudioSource(null);
    setText('');
    
    if (lastMessageId.current) interruptedMessages.current.add(lastMessageId.current);
    setTimeout(() => {
      appendToSpeakingBuffer(['Go ahead...'], true); // TODO: Clear existing buffer AND ignore all potential incoming
    }, 100);
  }


  function onSessionsClick() {
    window.location = '/sessions';
  }

  function handleFeedbackDialogClose(data) {
    if (data) {
      sendFeedback(data, onSessionsClick)
    } else {
      onSessionsClick();
    }
  }

  return (
    <div className="full">
      <div className="topright">
          {(!isSessionActive()) && 
            <div className="side-button" onClick={onSessionsClick}>
              <ListAltIcon />
              <br/>
              <span>My Sessions</span>
            </div>
          }

          {isSessionActive() && !(initialQuestion.current || initialSay.current || initialBookId.current) &&
            <div className="side-button" onClick={onEndClick}>
                <StopCircle />
                <br/>
                <span>End Session</span>
            </div>
          } 
      </div>

      { initialSayTitle.current && 
        <div className='say-title'>
          { initialSayTitle.current }
        </div>
      }

      { contentPreview &&
        <div className="contentPreview">
          <a target="_blank" href={contentPreview.url}>
            <img src={ `${assetsUrl}/${contentPreview.preview_img}` }></img>
            <div>              
                <span>{contentPreview.title}</span>            
            </div>
            </a>
        </div>
      }

      <div className="bottom-area" style={{visibility: (appState == SPEAKING && initialBookId.current) ? 'hidden': 'visible' }}>
        { appState === READY &&
          <div className="indicators" onClick={onStartClick}>
            <PlayCircleOutlineIcon style={{cursor: 'pointer'}}  />
            <span>Begin Session</span>
          </div>
        }

        { appState === PROCESSING && !(initialQuestion.current || initialSay.current || initialBookId.current) && 
          <div className="indicators">
            <CircularProgress style={{ color: 'yellow' }} />
          </div>
        }

        { appState === RECOGNIZING && 
          <div className="indicators" onClick={onClickMicrophone} onTouchStart={onClickMicrophone} >
            <MicNoneIcon/>
          </div>
        }
        
        {appState == SPEAKING && !(initialQuestion.current || initialSay.current || initialBookId.current) &&
          <div className="indicators">
            <CancelIcon style={{ fontSize: 40, color: 'red', cursor: 'pointer' }} onClick={onInterruptClick}/>
          </div>
        }
        {isSessionActive() && <div id="text" >{text}</div>}
      </div>

      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={handleFeedbackDialogClose}
      />

      <ReactAudioPlayer
        src={audioSource}
        ref={audioPlayer}
        onEnded={playerEnded}
        onCanPlayThrough={playerReady}
      />
      
      <Canvas dpr={2} gl={{ powerPreference: "high-performance" }} onCreated={(ctx) => { ctx.gl.physicallyCorrectLights = true; }}>

        <OrthographicCamera 
          makeDefault
          zoom={2000}
          position={[.041, 1.62, 1]}
        />

        <Suspense fallback={null}>
          <Environment background={false} files={backgroundImage3D} />
        </Suspense>

        <Suspense fallback={null}>
          <Bg />
        </Suspense>

        <Suspense fallback={null}>
          <Avatar 
            speechBlendData={speechBlendData}
            playing={playing}
          />
        </Suspense>
      </Canvas>
      <Loader dataInterpolation={(p) => `Loading... please wait`}  />
    </div>
  ) // return
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/start" element={<IntroScreen/>} />
        <Route path="/topic/:topicName" element={<TopicIntro chatHost={chatHost} avatarUrl={avatarUrl}/>} />
        <Route path="/work" element={<WorkTopicIntro chatHost={chatHost} avatarUrl={avatarUrl}/>} />
        <Route path="/chat" element={<AvatarScreen />} />
        <Route path="/audiotest" element={<AudioTest />} />
        <Route path="/sessions" element={<SessionTableAccordion chatHost={chatHost} />} />
        <Route path="/profile" element={<PersonalityForm chatHost={chatHost} avatarUrl={avatarUrl}/>} />
        <Route path="/question" element={<QuestionForm/>} />
      </Routes>
    </BrowserRouter>
  )
}

export default App;
